import React, { useEffect, useState } from 'react';
import { Alert, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Google } from '@mui/icons-material';
import { Prepa5LogoBlack } from 'assets';
import { apiErrorMessage, request } from '../../api/http';
import { apiUrl } from '../../config/api';
import { EUserRole, IUser } from '../../types/user';
import { setUser } from '../../state-management/slices/Global';
import { RootState } from '../../state-management/store';
import { SessionLoading } from '../../components/SessionBootstrap/styles';
import { ROOT } from '../../navigation/constants';
import {
	PageBackground,
	LoginCard,
	LogoContainer,
	WelcomeSection,
	GoogleButton,
	StyledDivider,
	DebugSection,
	DebugGrid,
	DebugButton,
} from './styles';

const DEV_ROLES: {
	label: string;
	role: EUserRole;
	color: 'primary' | 'success' | 'warning' | 'error';
}[] = [
	{ label: 'Alumno', role: EUserRole.ALUMNO, color: 'primary' },
	{ label: 'Docente', role: EUserRole.DOCENTE, color: 'success' },
	{ label: 'Auditor', role: EUserRole.AUDITOR, color: 'warning' },
	{ label: 'Administrador', role: EUserRole.ADMINISTRADOR, color: 'error' },
];

const showDevLogin = process.env.NODE_ENV === 'development';

/** Keys match the `reason` the API appends to `/login?auth=error`. */
const LOGIN_ERROR_MESSAGES: Record<string, string> = {
	unauthorized_domain:
		'Correo no autorizado para este portal. Use una cuenta institucional UDG (@alumnos.udg.mx o @academicos.udg.mx).',
	account_deactivated:
		'Su cuenta está desactivada. Contacte a la administración de la escuela.',
	missing_email: 'Google no compartió su correo. Intente de nuevo.',
	cancelled: 'Se canceló el inicio de sesión con Google.',
};
const DEFAULT_LOGIN_ERROR = 'No se pudo iniciar sesión. Intente de nuevo más tarde.';

const LoginPage: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const user = useSelector((state: RootState) => state.global.user);
	const sessionReady = useSelector((state: RootState) => state.global.sessionReady);
	const [searchParams, setSearchParams] = useSearchParams();
	const [oauthError, setOauthError] = useState<string | null>(null);
	const [pendingRole, setPendingRole] = useState<EUserRole | null>(null);

	const handleDevLogin = async (role: EUserRole): Promise<void> => {
		if (pendingRole) return;
		setPendingRole(role);
		setOauthError(null);
		try {
			const user = await request<IUser>('/api/auth/dev-login', {
				method: 'POST',
				body: JSON.stringify({ role }),
			});
			dispatch(setUser(user));
			navigate(ROOT);
		} catch (error) {
			setOauthError(apiErrorMessage(error));
			setPendingRole(null);
		}
	};

	const handleGoogleLogin = () => {
		window.location.assign(apiUrl('/api/auth/google'));
	};

	useEffect(() => {
		const auth = searchParams.get('auth');
		if (auth === 'error') {
			setOauthError(LOGIN_ERROR_MESSAGES[searchParams.get('reason') ?? ''] ?? DEFAULT_LOGIN_ERROR);
			setSearchParams({});
			return;
		}
		if (auth !== 'success') return;

		let cancelled = false;
		const completeOAuth = async (): Promise<void> => {
			try {
				const res = await fetch(apiUrl('/api/auth/me'), { credentials: 'include' });
				if (cancelled) return;
				if (!res.ok) {
					setOauthError('No se pudo completar el inicio de sesión.');
					setSearchParams({});
					return;
				}
				const user = (await res.json()) as IUser;
				dispatch(setUser(user));
				setSearchParams({});
				navigate(ROOT);
			} catch {
				if (!cancelled) {
					setOauthError('Error de red al verificar la sesión.');
					setSearchParams({});
				}
			}
		};

		void completeOAuth();
		return () => {
			cancelled = true;
		};
	}, [searchParams, dispatch, navigate, setSearchParams]);

	useEffect(() => {
		if (!sessionReady || !user) return;
		if (searchParams.get('auth') === 'error') return;
		navigate(ROOT, { replace: true });
	}, [sessionReady, user, searchParams, navigate]);

	const authError = searchParams.get('auth') === 'error';
	if ((!sessionReady && !authError) || (sessionReady && user && !authError)) {
		return (
			<SessionLoading>
				<Typography color='text.secondary'>Cargando...</Typography>
			</SessionLoading>
		);
	}

	return (
		<PageBackground>
			<LoginCard>
				{oauthError && (
					<Alert severity='error' onClose={() => setOauthError(null)} sx={{ width: '100%' }}>
						{oauthError}
					</Alert>
				)}
				{/* Logo & School Name */}
				<LogoContainer>
					<img src={Prepa5LogoBlack} alt='Prepa5 Logo' width={72} height={72} />
					<div>
						<Typography variant='h2' color='primary.main'>
							Escuela Preparatoria 5
						</Typography>
						<Typography variant='body2' color='text.secondary'>
							Portal para maestros y estudiantes
						</Typography>
					</div>
				</LogoContainer>

				{/* Welcome */}
				<WelcomeSection>
					<Typography variant='h5' fontWeight={600}>
						Bienvenido
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Para acceder al portal, conéctese con su cuenta Institucional de
						Google.
					</Typography>
				</WelcomeSection>

				{/* Google Login */}
				<GoogleButton
					variant='contained'
					startIcon={<Google />}
					onClick={handleGoogleLogin}
				>
					Continuar con Google
				</GoogleButton>

				{showDevLogin && (
					<>
						<StyledDivider>modo desarrollo</StyledDivider>
						<DebugSection>
							<Typography variant='caption' color='text.disabled' textAlign='center'>
								Iniciar sesión con un usuario local
							</Typography>
							<DebugGrid>
								{DEV_ROLES.map(({ label, role, color }) => (
									<DebugButton
										key={role}
										variant='outlined'
										color={color}
										disabled={pendingRole !== null}
										onClick={() => void handleDevLogin(role)}
									>
										{label}
									</DebugButton>
								))}
							</DebugGrid>
						</DebugSection>
					</>
				)}
			</LoginCard>
		</PageBackground>
	);
};

export default LoginPage;
